import { createElement, useEffect } from 'react';
import type { ComponentType, ReactNode } from 'react';

import { recordRumMilestone } from './index';
import { RumMilestone } from './types';

/**
 * Reports that a route's page component has committed its first render —
 * `route_page_rendered`, the only initial-load milestone that contains any of
 * the page's own cost, and what `page_ready` resolves to on Hydra-native
 * routes.
 *
 * Every route page has to call this, directly or otherwise, or it drops out of
 * the page_ready series entirely. `lazyRoute` calls it for the 145 pages behind
 * it; a route whose page is a plain static import (`/login/$`,
 * `/join-request`, `/integrations/all/overview`, ...) has no wrapper to do it
 * and reports through {@link RumPageRendered} or {@link withRumPageRendered}
 * instead.
 *
 * A route that only ever redirects is not one of them — `/logout/$` and
 * `/academy-login` render a loader and navigate away, never the page the
 * reader asked for, and are exempted in the `require-rum-page-rendered`
 * config rather than reported.
 *
 * The reporting half of the pair `@/services/rum` exports as
 * `expectRumPageRender`: that one announces from `lazyRoute` that a page
 * render is coming so the quiet-period flush waits for it, this one says it
 * arrived.
 *
 * An effect rather than a render-time call because the milestone means the
 * render *committed*: for a lazily-loaded page the subtree is not committed
 * while it is suspended, so the effect first runs on the render that actually
 * puts the page on screen. Recording is write-once, so a re-render or
 * StrictMode's double invoke keeps the first timestamp.
 */
export function useRumPageRendered(): void {
  useEffect(() => {
    recordRumMilestone(RumMilestone.RoutePageRendered);
  }, []);
}

type RumPageRenderedProps = {
  /** The page this route puts on screen. */
  children: ReactNode;
};

/**
 * Marks the element it wraps as the page this route puts on screen, reporting
 * `route_page_rendered` when that page commits.
 *
 * The wrapping form, for the common case where a page is rendered inside route
 * gates — a migration hand-off, a permission check, a canonical-spelling
 * redirect — each of which can render a loader, a `<Navigate>` or `null`
 * instead of the page. Wrapping the *route* component in
 * {@link withRumPageRendered} timestamps whichever of those branches commits
 * first, and the milestone is write-once, so the wrong value wins. Wrapping the
 * page element itself cannot: it only mounts when the page does.
 *
 * ```tsx
 * <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
 *   <RumPageRendered>
 *     <PeopleListPage />
 *   </RumPageRendered>
 * </MigrationRouteView>
 * ```
 *
 * Use {@link withRumPageRendered} instead where the route component *is* the
 * page and there is no JSX to wrap (`component: withRumPageRendered(Page)`).
 */
export function RumPageRendered({ children }: RumPageRenderedProps) {
  useRumPageRendered();

  return children;
}

/**
 * Wraps a route component so it reports `route_page_rendered` on commit.
 *
 * For a static-import page where the route component *is* the page and there
 * is no JSX to wrap — `/login/$`, `/join-request`, the `/integrations/all`
 * and sql-sync tabs. Without it those pages render normally but leave the
 * page_ready series entirely, and the remaining p75 is computed over
 * lazyRoute pages only.
 *
 * Use {@link RumPageRendered} instead wherever the route component can render
 * something other than the page — a gate's loader, a `<Navigate>`, `null`.
 * This form timestamps whichever of those commits first, and the milestone is
 * write-once, so the wrong value wins.
 *
 * Deliberately NOT applied to a route whose component redirects
 * (`SafeNavigate`), hosts an embedded app (the child-app milestones own those)
 * or renders the not-found page: none of them is the page the user asked for
 * becoming usable, and marking them ready is the overstatement this milestone
 * exists to remove.
 */
export function withRumPageRendered<Props extends object>(Component: ComponentType<Props>) {
  function WrappedRumPageRendered(props: Props) {
    useRumPageRendered();
    return createElement(Component, props);
  }
  WrappedRumPageRendered.displayName = `RumPageRendered(${Component.displayName ?? Component.name})`;
  return WrappedRumPageRendered;
}
